/**
 * Black Matte VTC - Partner Invoices List
 * Pure view layer - ALL data from API
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  FileText,
  Calendar,
  AlertTriangle,
  Eye,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

// Invoice status badge - pure display
const InvoiceStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: 'Draft', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20', icon: Clock },
    issued: { label: 'Issued', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: FileText },
    sent: { label: 'Sent', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertCircle },
    paid: { label: 'Paid', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle },
    partially_paid: { label: 'Partial', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertCircle },
    overdue: { label: 'Overdue', className: 'bg-red-500/10 text-red-400 border-red-500/20', icon: AlertTriangle },
    cancelled: { label: 'Cancelled', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20', icon: XCircle },
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`flex items-center gap-1 ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const PartnerInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const response = await partnerService.getInvoices(100, status);
      setInvoices(response.invoices || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  // Count overdue invoices for banner
  const overdueCount = invoices.filter(inv => inv.status === 'overdue').length;

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-invoices">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Invoices</h1>
        <p className="text-neutral-500">
          View and manage your billing invoices
        </p>
      </div>

      {/* Overdue Warning */}
      {overdueCount > 0 && (
        <div 
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3"
          data-testid="overdue-banner"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">
              You have {overdueCount} overdue invoice{overdueCount > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-red-400/80 mt-1">
              Please settle overdue invoices to continue creating bookings.
            </p>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="ml-auto border-red-500/30 text-red-400 hover:bg-red-500/10"
            asChild
          >
            <Link to="/partner/payments">Make Payment</Link>
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-neutral-800 border-neutral-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-800 border-neutral-700">
                <SelectItem value="all">All Invoices</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="border-neutral-700 hover:bg-neutral-800"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full bg-neutral-800" />
              ))}
            </div>
          ) : invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">Invoice #</TableHead>
                    <TableHead className="text-neutral-400">Period</TableHead>
                    <TableHead className="text-neutral-400">Due Date</TableHead>
                    <TableHead className="text-neutral-400">Amount</TableHead>
                    <TableHead className="text-neutral-400">Paid</TableHead>
                    <TableHead className="text-neutral-400">Due</TableHead>
                    <TableHead className="text-neutral-400">Status</TableHead>
                    <TableHead className="text-neutral-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow 
                      key={invoice.id} 
                      className="border-neutral-800 hover:bg-neutral-800/50"
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <TableCell className="font-medium text-white">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {invoice.period_start && invoice.period_end ? (
                          <span className="text-sm">
                            {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-neutral-300">
                          <Calendar className="h-4 w-4 text-neutral-500" />
                          <span>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-white">
                        €{invoice.total_amount?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell className="text-emerald-400">
                        €{invoice.amount_paid?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell className={`font-medium ${invoice.amount_due > 0 ? 'text-amber-400' : 'text-neutral-400'}`}>
                        €{invoice.amount_due?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell>
                        <InvoiceStatusBadge status={invoice.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="text-neutral-400 hover:text-white"
                        >
                          <Link to={`/partner/invoices/${invoice.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-neutral-700 mx-auto mb-3" />
              <p className="text-neutral-500">
                {statusFilter !== 'all' ? 'No invoices match this filter' : 'No invoices yet'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Count */}
      <div className="text-sm text-neutral-500">
        Showing {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default PartnerInvoices;
